import { createContext, useContext } from 'react';
import { PageAnimationTypes } from '@zola/zola-ui/src/animations/PageAnimations/types';
import { FoilAnimationColors } from '@zola/zola-ui/src/animations/TextFoil/TextFoil';
import type {
  WSkuPreview,
  OverlayEffectStyleCustomizationRequest,
  WWebsiteEffectView,
} from '@zola/svc-web-api-ts-client';

export type EasingFunction = 'ease-in' | 'linear' | 'ease-out' | 'ease-in-out';

type AnimationContextType = {
  /**
   *  Does the user's account have animations enabled - edit website only
   * */
  animationsEnabledForAccount: boolean;
  enableAnimationsAfterPurchase: () => void;
  /**
   * If on edit website, this is fetched in the provider and will not necessary reflect the "true" state of the website
   * i.e. in edit website appliedAnimations can have a value even if animations have not been purchased/enabled.
   *
   * For user's own public website, this value is fetched server side and will sometimes be overridden in the provider
   * i.e. if the user has not purchased animations the SSR value will be empty, but the provider may fetch and fill this value to show a preview.
   *
   * For a public website that is NOT the user's own, this value is fetched server side and will ALWAYS reflect the "true" state
   * i.e. we can rely on that if this value is empty, the website's owner has not purchased animations; if there is a value they have.
   *
   * The same logic is true for foilAnimation, pageAnimation, and overlayAnimation, as they are all derived from appliedAnimations
   */
  appliedAnimations: WWebsiteEffectView[];
  foilAnimation?: FoilAnimationColors;
  pageAnimation?: PageAnimationTypes;
  overlayAnimation?: WWebsiteEffectView;

  availableAnimations: WWebsiteEffectView[];
  setAppliedAnimations?: (animations: WWebsiteEffectView[]) => void;
  experimentVariation?: string;
  animationProduct: Pick<WSkuPreview, 'price_cents' | 'id'>;
  /**
   * True when certain overlay animations are applied that would overlap with the welcome message
   */
  hideHeroTitleText?: boolean;
  forcePageAnimationUpdate?: () => void;
  forceOverlayUpdate?: () => void;
  /**
   * Used to force react to re-render the animation on edit website
   */
  pageAnimationId?: number;
  /**
   * Used to force react to re-render the animation on edit website
   */
  overlayAnimationId?: number;
  isMobileDevice?: boolean;
  updateAppliedAnimations: (animation: WWebsiteEffectView, isSelected: boolean) => void;
  updateOverlayAnimationCustomizations: (
    overlayCustomization: OverlayEffectStyleCustomizationRequest
  ) => Promise<void | null>;
  isEditWebsite?: boolean;
  showFoilAnimation?: boolean;
  setShowAnimationPreview?: (show: boolean) => void;
  /**
   * Should we ignore any applied animations on edit website
   *
   * Case: user has applied animations but not purchased them, we want to know they have applied animations but don't actually want to show them
   */
  hideEffects?: boolean;
  showAnimationPreview: boolean;
};

export const defaultAnimationContextValues: AnimationContextType = {
  animationsEnabledForAccount: false,
  enableAnimationsAfterPurchase: () => null,
  appliedAnimations: [],
  availableAnimations: [],
  setAppliedAnimations: () => null,
  experimentVariation: undefined,
  animationProduct: {} as WSkuPreview,
  foilAnimation: undefined,
  pageAnimation: undefined,
  showFoilAnimation: false,
  updateAppliedAnimations: () => null,
  updateOverlayAnimationCustomizations: () => Promise.resolve(),
  isMobileDevice: undefined,
  forcePageAnimationUpdate: () => null,
  forceOverlayUpdate: () => null,
  pageAnimationId: undefined,
  overlayAnimationId: undefined,
  hideHeroTitleText: undefined,
  overlayAnimation: undefined,
  isEditWebsite: false,
  hideEffects: false,
  setShowAnimationPreview: () => null,
  showAnimationPreview: false,
};

export const AnimationContext = createContext<AnimationContextType>(defaultAnimationContextValues);

export const useAnimationContext = () => {
  const context = useContext(AnimationContext);
  if (context === undefined) {
    throw new Error('useAnimationContext must be used within an AnimationProvider');
  }
  return context;
};
